import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { trekAPI, adminAPI } from '../services/api';
import { Trash2, Plus, GripVertical, X } from 'lucide-react';
import Sortable from 'sortablejs';

function StoryItemsList({ story, handleDeleteItem, handleReorderItems }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const sortable = Sortable.create(containerRef.current, {
      animation: 150,
      handle: '.story-item',
      onEnd: (evt) => {
        if (evt.oldIndex === evt.newIndex) return;
        
        // Temporarily revert the DOM change that Sortable just made, 
        // so React can take over and re-render the new state properly.
        const itemEl = evt.item;
        const parent = evt.from;
        if (evt.oldIndex < evt.newIndex) {
          parent.insertBefore(itemEl, parent.children[evt.oldIndex]);
        } else {
          parent.insertBefore(itemEl, parent.children[evt.oldIndex + 1]);
        }

        handleReorderItems(story.id, evt.oldIndex, evt.newIndex);
      }
    });
    return () => sortable.destroy();
  }, [story.items, story.id, handleReorderItems]);

  return (
    <div ref={containerRef} style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }}>
      {(story.items || []).map((item, idx) => (
        <div 
          key={`${item.url}-${idx}`} 
          className="story-item"
          style={{ position: 'relative', width: '60px', height: '100px', flexShrink: 0, borderRadius: '4px', overflow: 'hidden', backgroundColor: '#cbd5e1', cursor: 'grab' }}
        >
          {item.type === 'video' ? (
            <video src={item.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <img src={item.url} alt={`Item ${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDeleteItem(story.id, idx); }}
            style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', padding: '2px', cursor: 'pointer', zIndex: 10 }}
          >
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function WebstoriesManager({ trekId, onError }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const listRef = useRef(null);
  const [uploadTargetStoryId, setUploadTargetStoryId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const queryKey = ['trek-webstories', trekId];

  const { data: res, isLoading } = useQuery(queryKey, () => trekAPI.listWebstories(trekId), {
    enabled: !!trekId,
    onError
  });
  const webstories = res?.data?.data || [];

  const createMutation = useMutation((data) => trekAPI.createWebstory(trekId, data), {
    onSuccess: () => queryClient.invalidateQueries(queryKey),
    onError
  });

  const updateMutation = useMutation(({ id, data }) => trekAPI.updateWebstory(trekId, id, data), {
    // Optimistically update the UI
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries(queryKey);
      const previousData = queryClient.getQueryData(queryKey);
      
      if (previousData?.data?.data) {
        queryClient.setQueryData(queryKey, {
          ...previousData,
          data: {
            ...previousData.data,
            data: previousData.data.data.map(story => 
              story.id === id ? { ...story, ...data } : story
            )
          }
        });
      }
      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      if (onError) onError(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey);
    }
  });

  const deleteMutation = useMutation((id) => trekAPI.deleteWebstory(trekId, id), {
    onSuccess: () => queryClient.invalidateQueries(queryKey),
    onError
  });

  useEffect(() => {
    if (listRef.current && webstories.length > 0) {
      const sortable = Sortable.create(listRef.current, {
        handle: '.drag-handle',
        animation: 150,
        onEnd: (evt) => {
          if (evt.oldIndex === evt.newIndex) return;
          
          // Revert DOM so React can re-render
          const itemEl = evt.item;
          const parent = evt.from;
          if (evt.oldIndex < evt.newIndex) {
            parent.insertBefore(itemEl, parent.children[evt.oldIndex]);
          } else {
            parent.insertBefore(itemEl, parent.children[evt.oldIndex + 1]);
          }

          const newArray = [...webstories];
          const [movedItem] = newArray.splice(evt.oldIndex, 1);
          newArray.splice(evt.newIndex, 0, movedItem);
          
          // Optimistically update all in cache
          queryClient.setQueryData(queryKey, old => {
            if (!old?.data?.data) return old;
            return {
              ...old,
              data: {
                ...old.data,
                data: newArray.map((s, i) => ({ ...s, order: i }))
              }
            };
          });

          newArray.forEach((story, idx) => {
            if (story.order !== idx) {
              // we don't need onMutate here because we just updated the whole array
              trekAPI.updateWebstory(trekId, story.id, { order: idx }).catch(() => {
                queryClient.invalidateQueries(queryKey); // refresh on fail
              });
            }
          });
        }
      });
      return () => sortable.destroy();
    }
  }, [webstories, trekId, queryClient]);

  const handleAddStory = () => {
    createMutation.mutate({
      title: 'New Story',
      order: webstories.length,
      items: []
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this webstory?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleUpdate = (id, field, value) => {
    updateMutation.mutate({ id, data: { [field]: value } });
  };

  const handleAddItem = (storyId, url, type = 'image') => {
    const story = webstories.find(s => s.id === storyId);
    if (!story) return;
    const newItems = [...(story.items || []), { url, type }];
    updateMutation.mutate({ id: storyId, data: { items: newItems } });
  };

  const handleDeleteItem = (storyId, itemIndex) => {
    const story = webstories.find(s => s.id === storyId);
    if (!story) return;
    const newItems = story.items.filter((_, idx) => idx !== itemIndex);
    updateMutation.mutate({ id: storyId, data: { items: newItems } });
  };

  const handleReorderItems = (storyId, oldIndex, newIndex) => {
    const story = webstories.find(s => s.id === storyId);
    if (!story) return;
    const newItems = [...(story.items || [])];
    const [movedItem] = newItems.splice(oldIndex, 1);
    newItems.splice(newIndex, 0, movedItem);
    updateMutation.mutate({ id: storyId, data: { items: newItems } });
  };

  const onFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTargetStoryId) return;

    try {
      setIsUploading(true);
      const isVideo = file.type.startsWith('video/');
      const apiCall = isVideo ? adminAPI.uploadMedia(file) : adminAPI.uploadImage(file);
      const res = await apiCall;
      const url = res?.data?.data?.url || res?.data?.url;
      
      if (!url) throw new Error("Could not retrieve URL from upload response");

      handleAddItem(uploadTargetStoryId, url, isVideo ? 'video' : 'image');
    } catch (err) {
      if (onError) onError(err);
      else console.error(err);
    } finally {
      setIsUploading(false);
      setUploadTargetStoryId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerUpload = (storyId) => {
    setUploadTargetStoryId(storyId);
    if (fileInputRef.current) {
      fileInputRef.current.accept = 'image/*,video/*';
      fileInputRef.current.click();
    }
  };

  if (isLoading) return <div>Loading stories...</div>;

  return (
    <div className="webstories-manager" style={{ marginTop: '20px' }}>
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        onChange={onFileSelect} 
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <h3 className="module-section-title" style={{ margin: 0 }}>Webstories (Highlights)</h3>
        <button type="button" className="module-btn" onClick={handleAddStory}>
          <Plus size={16} /> Add Story
        </button>
      </div>

      {isUploading && <div style={{ color: '#f59e0b', marginBottom: '10px' }}>Uploading...</div>}

      <div ref={listRef} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {webstories.map((story, index) => (
          <div key={story.id} data-id={story.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', background: '#f8fafc' }}>
            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', alignItems: 'center' }}>
              <div className="drag-handle" style={{ cursor: 'grab', color: '#94a3b8' }}>
                <GripVertical size={24} />
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <input
                  type="text"
                  value={story.title}
                  onChange={(e) => handleUpdate(story.id, 'title', e.target.value)}
                  className="module-input"
                  style={{ fontWeight: 'bold', fontSize: '16px', border: 'none', background: 'transparent', padding: 0 }}
                  placeholder="Story Title"
                />
              </div>
              <button type="button" onClick={() => handleDelete(story.id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>
                <Trash2 size={20} />
              </button>
            </div>

            <div style={{ marginLeft: '40px' }}>
              <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '10px' }}>STORY ITEMS (Images/Videos) - First item is cover</p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <StoryItemsList 
                  story={story} 
                  handleDeleteItem={handleDeleteItem} 
                  handleReorderItems={handleReorderItems} 
                />
                <button 
                  type="button" 
                  onClick={() => triggerUpload(story.id)}
                  style={{ width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%', border: '1px dashed #94a3b8', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b', marginLeft: '5px' }}
                  title="Add Image or Video"
                >
                  <Plus size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
